import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import Navbar from "../component/Navbar";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { CircularProgress } from "@mui/material";

// Styled components
const Container = styled.div`
  padding: 20px;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 24px;
  margin-bottom: 20px;
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 30px;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: 10px 20px;
  background-color: ${props => props.active ? '#007bff' : '#f8f9fa'};
  color: ${props => props.active ? 'white' : '#212529'};
  border: 1px solid #dee2e6;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: ${props => props.active ? 'bold' : 'normal'};

  &:hover {
    background-color: ${props => props.active ? '#0056b3' : '#e9ecef'};
  }
`;

const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
  width: 100%;
`;

const FilterLabel = styled.label`
  font-weight: bold;
  margin-bottom: 8px;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  width: 100%;
  max-width: 300px;
  margin-bottom: 15px;
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  width: 100%;
  margin-bottom: 20px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 200px;
`;

const TableWrapper = styled.div`
  margin-top: 20px;
  overflow-x: auto;
`;

const GrowthCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
`;

const GrowthValue = styled.span`
  color: ${props => props.isPositive ? '#28a745' : '#dc3545'};
  font-weight: bold;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  margin: 20px 0;
  padding: 10px;
  background-color: #f8d7da;
  border-radius: 4px;
`;

function SalesPerformance() {
  const [period, setPeriod] = useState("1D"); // Default to daily view
  const [salesData, setSalesData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [processingLabs, setProcessingLabs] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedProcessingLab, setSelectedProcessingLab] = useState("all");
  const [selectedAgent, setSelectedAgent] = useState("all");
  const navigate = useNavigate();

  const fetchSalesData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post("https://apiv2.labridge.in/admin/api/sales-performance", {
        period
      });
      
      if (response.data.success) {
        setSalesData(response.data.data);
        setFilteredData(response.data.data);
      } else {
        setError("Failed to fetch sales data");
      }
    } catch (err) {
      console.error("Error fetching sales data:", err);
      setError("An error occurred while fetching sales data");
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    const isLoggedIn = JSON.parse(localStorage.getItem("loginData"));
    if (!isLoggedIn || !isLoggedIn.access.includes("5")) {
      navigate("/login");
    } else {
      fetchSalesData();
    }
  }, [fetchSalesData, navigate]);

  // Extract unique processing labs and agents from sales data
  useEffect(() => {
    if (salesData.length > 0) {
      // Extract unique processing labs
      const uniqueProcessingLabs = [...new Set(salesData
        .filter(item => item.processingLab && item.processingLab.id)
        .map(item => item.processingLab.id))];
      
      // Extract unique agents
      const uniqueAgents = [...new Set(salesData
        .filter(item => item.agent)
        .map(item => {
          if (typeof item.agent === 'string') {
            return item.agent;
          }
          return item.agent.name || 'Unknown';
        }))];
      
      setProcessingLabs(uniqueProcessingLabs);
      setAgents(uniqueAgents);
    }
  }, [salesData]);

  // Apply filters to data
  useEffect(() => {
    if (salesData.length > 0) {
      let filtered = [...salesData];
      
      // Filter by processing lab
      if (selectedProcessingLab !== "all") {
        filtered = filtered.filter(item => 
          item.processingLab && 
          item.processingLab.id === selectedProcessingLab
        );
      }
      
      // Filter by agent
      if (selectedAgent !== "all") {
        filtered = filtered.filter(item => {
          if (typeof item.agent === 'string') {
            return item.agent === selectedAgent;
          }
          return item.agent.name === selectedAgent;
        });
      }
      
      setFilteredData(filtered);
      setPage(0); // Reset to first page when filters change
    }
  }, [salesData, selectedProcessingLab, selectedAgent]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatGrowth = (value) => {
    return value.toFixed(2);
  };

  const renderGrowthIndicator = (value) => {
    if (value === 0) return null;
    
    return value > 0 ? (
      <ArrowUpwardIcon style={{ color: '#28a745' }} />
    ) : (
      <ArrowDownwardIcon style={{ color: '#dc3545' }} />
    );
  };

  const handleProcessingLabChange = (e) => {
    setSelectedProcessingLab(e.target.value);
  };

  const handleAgentChange = (e) => {
    setSelectedAgent(e.target.value);
  };

  // Get processing lab name by ID
  const getProcessingLabName = (id) => {
    const lab = salesData.find(item => 
      item.processingLab && item.processingLab.id === id
    );
    return lab ? lab.processingLab.name : 'Unknown Lab';
  };

  // Get agent name by ID
  const getAgentName = (id) => {
    return id; // Since we're using the name directly as the ID
  };

  return (
    <div>
      <div className="wrapper">
        <Navbar />
        <div id="content">
          <Container>
            <Title>Sales Performance</Title>
            
            <FilterContainer>
              <FilterButton 
                active={period === "1D"} 
                onClick={() => setPeriod("1D")}
              >
                Daily
              </FilterButton>
              <FilterButton 
                active={period === "1W"} 
                onClick={() => setPeriod("1W")}
              >
                Weekly
              </FilterButton>
              <FilterButton 
                active={period === "1M"} 
                onClick={() => setPeriod("1M")}
              >
                Monthly
              </FilterButton>
            </FilterContainer>

            <FilterSection>
              <FilterRow>
                <FilterGroup>
                  <FilterLabel htmlFor="processingLab">Processing Lab</FilterLabel>
                  <FilterSelect 
                    id="processingLab" 
                    value={selectedProcessingLab} 
                    onChange={handleProcessingLabChange}
                  >
                    <option value="all">All Processing Labs</option>
                    {processingLabs.map(labId => (
                      <option key={labId} value={labId}>
                        {getProcessingLabName(labId)}
                      </option>
                    ))}
                  </FilterSelect>
                </FilterGroup>

                <FilterGroup>
                  <FilterLabel htmlFor="agent">Agent</FilterLabel>
                  <FilterSelect 
                    id="agent" 
                    value={selectedAgent} 
                    onChange={handleAgentChange}
                  >
                    <option value="all">All Agents</option>
                    {agents.map(agentName => (
                      <option key={agentName} value={agentName}>
                        {agentName}
                      </option>
                    ))}
                  </FilterSelect>
                </FilterGroup>
              </FilterRow>
            </FilterSection>

            {loading ? (
              <LoadingContainer>
                <CircularProgress />
              </LoadingContainer>
            ) : error ? (
              <ErrorMessage>{error}</ErrorMessage>
            ) : (
              <TableWrapper>
                <TableContainer component={Paper}>
                  <Table sx={{ minWidth: 650 }} aria-label="sales performance table">
                    <TableHead>
                      <TableRow>
                        <TableCell><b>Lab Name</b></TableCell>
                        <TableCell><b>Processing Lab</b></TableCell>
                        <TableCell><b>Agent</b></TableCell>
                        <TableCell align="right"><b>Amount</b></TableCell>
                        <TableCell align="right"><b>Orders</b></TableCell>
                        <TableCell align="right"><b>Samples</b></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(rowsPerPage > 0
                        ? filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        : filteredData
                      ).map((row, index) => (
                        <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell component="th" scope="row">
                            {row.labName}
                          </TableCell>
                          <TableCell>{row.processingLab.name}</TableCell>
                          <TableCell>
                            {typeof row.agent === 'string' ? row.agent : row.agent.name}
                          </TableCell>
                          <TableCell align="right">
                            <GrowthCell>
                              {formatCurrency(row.currentPeriod.totalAmount)}
                              {renderGrowthIndicator(row.growth.amount)}
                              <GrowthValue isPositive={row.growth.amount > 0}>
                                {row.growth.amount !== 0 && `${formatGrowth(row.growth.amount)}%`}
                              </GrowthValue>
                            </GrowthCell>
                          </TableCell>
                          <TableCell align="right">
                            <GrowthCell>
                              {row.currentPeriod.totalOrders}
                              {renderGrowthIndicator(row.growth.orders)}
                              <GrowthValue isPositive={row.growth.orders > 0}>
                                {row.growth.orders !== 0 && `${formatGrowth(row.growth.orders)}%`}
                              </GrowthValue>
                            </GrowthCell>
                          </TableCell>
                          <TableCell align="right">
                            <GrowthCell>
                              {row.currentPeriod.totalSamples}
                              {renderGrowthIndicator(row.growth.samples)}
                              <GrowthValue isPositive={row.growth.samples > 0}>
                                {row.growth.samples !== 0 && `${formatGrowth(row.growth.samples)}%`}
                              </GrowthValue>
                            </GrowthCell>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={filteredData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </TableContainer>
              </TableWrapper>
            )}
          </Container>
        </div>
      </div>
    </div>
  );
}

export default SalesPerformance; 